/* SizeInventoryEditor — small drop-in editor that lets an admin track stock
 * per apparel size (S, M, L, XL, 2XL, etc.). Mounted inside the AdminShopTab
 * product form, below the freeform Variations editor.
 *
 * Data shape: object map { S: 5, M: 8, L: 3, XL: 0, "2XL": 2 }
 * - Empty object = "size tracking off" → customer page renders normally
 *   (no size gate at checkout).
 * - Any non-zero entries = "size tracking on" → checkout requires a size and
 *   decrements that size's stock atomically.
 *
 * The backend ignores unknown sizes and uppercases them; this UI lets admins
 * add custom sizes too (e.g. "OS" for one-size).
 */
import React, { useState } from 'react';
import { Plus, Shirt, Trash2 } from 'lucide-react';

const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const SizeInventoryEditor = ({ value, onChange }) => {
  const sizes = value || {};
  const sizeKeys = Object.keys(sizes);
  const enabled = sizeKeys.length > 0;
  const [customSize, setCustomSize] = useState('');

  const update = (k, n) => {
    const next = { ...sizes };
    next[k] = Math.max(0, parseInt(n, 10) || 0);
    onChange(next);
  };

  const remove = (k) => {
    const next = { ...sizes };
    delete next[k];
    onChange(next);
  };

  const add = (k) => {
    const norm = (k || '').trim().toUpperCase();
    if (!norm || sizes[norm] !== undefined) return;
    onChange({ ...sizes, [norm]: 0 });
  };

  const total = sizeKeys.reduce((s, k) => s + (parseInt(sizes[k], 10) || 0), 0);

  return (
    <div className="mb-3 bg-[#F8F9FA] rounded-lg p-3 border border-[#E5E7EB]"
      data-testid="size-inventory-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-[#4B5563] flex items-center gap-1.5">
          <Shirt className="w-3.5 h-3.5 text-[#BA1826]" />
          Per-Size Inventory (apparel)
          {enabled && (
            <span className="text-[10px] text-[#0A0A0A] bg-white border border-[#E5E7EB] rounded-full px-2 py-0.5 ml-2 font-mono">
              {total} total
            </span>
          )}
        </label>
        {!enabled && (
          <button type="button"
            onClick={() => onChange({ S: 0, M: 0, L: 0, XL: 0 })}
            className="text-[10px] text-[#BA1826] font-semibold hover:underline flex items-center gap-0.5"
            data-testid="size-inv-enable">
            <Plus className="w-3 h-3" /> Enable size tracking
          </button>
        )}
        {enabled && (
          <button type="button"
            onClick={() => onChange({})}
            className="text-[10px] text-[#9CA3AF] hover:text-[#BA1826] hover:underline"
            data-testid="size-inv-disable">
            Turn off
          </button>
        )}
      </div>

      {!enabled && (
        <p className="text-[10px] text-[#9CA3AF]">
          For T-shirts, hoodies, etc. — set stock by size so customers can
          only buy sizes that are actually available.
        </p>
      )}

      {enabled && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {sizeKeys.map((k) => (
              <div key={k} className="bg-white border border-[#E5E7EB] rounded p-2"
                data-testid={`size-row-${k}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-bold text-[#0A0A0A]">{k}</span>
                  <button type="button" onClick={() => remove(k)}
                    className="text-[#9CA3AF] hover:text-[#BA1826]"
                    aria-label={`Remove size ${k}`}
                    data-testid={`size-remove-${k}`}>
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
                <input type="number" min="0" value={sizes[k]}
                  onChange={(e) => update(k, e.target.value)}
                  className="w-full px-1.5 py-1 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
                  data-testid={`size-qty-${k}`} />
                {sizes[k] === 0 && (
                  <p className="text-[9px] text-amber-600 mt-1">Out of stock</p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-[#9CA3AF] mr-1">Add size:</span>
            {COMMON_SIZES.filter((s) => sizes[s] === undefined).map((s) => (
              <button key={s} type="button" onClick={() => add(s)}
                className="text-[10px] px-2 py-0.5 border border-dashed border-[#BA1826]/30 text-[#BA1826] rounded hover:bg-[#FEF2F2]"
                data-testid={`size-quick-${s}`}>
                + {s}
              </button>
            ))}
            <div className="flex items-center gap-1 ml-1">
              <input type="text" value={customSize}
                placeholder="Custom"
                onChange={(e) => setCustomSize(e.target.value)}
                className="w-20 px-2 py-0.5 text-[10px] border border-[#E5E7EB] rounded"
                data-testid="size-custom-input" />
              <button type="button"
                onClick={() => { add(customSize); setCustomSize(''); }}
                className="text-[10px] px-2 py-0.5 bg-[#0A0A0A] text-white rounded"
                data-testid="size-custom-add">
                Add
              </button>
            </div>
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-2">
            When per-size tracking is on, the main inventory field is ignored
            and the customer page hides out-of-stock sizes automatically.
          </p>
        </>
      )}
    </div>
  );
};

export default SizeInventoryEditor;
